<script setup>
import {computed, inject, ref, watch} from 'vue'
import {
    Dialog,
    DialogPanel, TransitionRoot,
} from '@headlessui/vue'
import {Link} from "@inertiajs/vue3";

let getTheme = inject('getTheme');
let setTheme = inject('setTheme');

const isOpen = ref(false)

function setIsOpen(value) {
    isOpen.value = value
}

const onChangeTheme = (ev) => {
    setTheme(ev.target.value);
}

const theme = computed(() => {
    return getTheme();
})

</script>

<template>
    <button type="button"
            @click="setIsOpen(true)"
            class="w-6 h-6 translate-x-[4px] flex items-center justify-center outline-none shadow-none dark:text-white">
        <span class="sr-only">Навигация</span>

        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M10 3H3V10H10V3Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M21 3H14V10H21V3Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M21 14H14V21H21V14Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M10 14H3V21H10V14Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
    </button>
    <TransitionRoot
        :show="isOpen"
        as="template"
        enter="duration-100 ease-out"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="duration-100 ease-in"
        leave-from="opacity-100"
        leave-to="opacity-0"
    >
        <Dialog @close="setIsOpen">
            <div class="fixed inset-0 bg-black/30 z-20" aria-hidden="true" />
            <DialogPanel class="fixed top-1 right-1 w-full max-w-xs bg-white dark:bg-bg-dark rounded-lg shadow-lg p-6 text-base font-semibold z-20">
                <button type="button"
                        @click="setIsOpen(false)"
                        class="absolute top-3 right-3 w-8 h-8 flex items-center justify-center hover:text-orange outline-none shadow-none"
                        tabindex="0"><span class="sr-only">Закрыть навигацию</span>
                    <svg viewBox="0 0 10 10" class="w-2.5 h-2.5 overflow-visible" aria-hidden="true">
                        <path d="M0 0L10 10M10 0L0 10" fill="none" stroke="currentColor" stroke-width="2"
                              stroke-linecap="round"></path>
                    </svg>
                </button>
                <ul class="space-y-6 text-black dark:text-white">
                    <li><Link href="/#main" class="block hover:text-orange"><span>Что это?</span></Link></li>
                    <li><Link href="/#player" class="block hover:text-orange"><span>Игрокам</span></Link></li>
                    <li><Link href="/#master" class="block hover:text-orange"><span>Мастеру</span></Link></li>
                    <li><Link href="/#features" class="block hover:text-orange"><span>Плюшки</span></Link></li>
                    <li><Link href="/#subscribe" class="block hover:text-orange"><span>Подписаться</span></Link></li>
                </ul>
                <div class="mt-6 pt-6 border-t border-gray-dark dark:border-gray-dark text-black dark:text-white">
                    <div class="flex items-center justify-between">
                        <label for="theme">Тема</label>
                        <div class="relative flex items-center ring-1 ring-slate-900/10 rounded-lg shadow-sm p-2 font-semibold dark:ring-0 dark:highlight-white/5">
                            <svg viewBox="0 0 24 24" fill="none" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-6 h-6 mr-2 dark:hidden">
                                <path d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" class="stroke-slate-400 dark:stroke-slate-500"></path>
                                <path d="M12 4v1M17.66 6.344l-.828.828M20.005 12.004h-1M17.66 17.664l-.828-.828M12 20.01V19M6.34 17.664l.835-.836M3.995 12.004h1.01M6 6l.835.836" class="stroke-slate-400 dark:stroke-slate-500"></path>
                            </svg>
                            <svg viewBox="0 0 24 24" fill="none" class="w-6 h-6 mr-2 hidden dark:block">
                                <path fill-rule="evenodd" clip-rule="evenodd" d="M17.715 15.15A6.5 6.5 0 0 1 9 6.035C6.106 6.922 4 9.645 4 12.867c0 3.94 3.153 7.136 7.042 7.136 3.101 0 5.734-2.032 6.673-4.853Z" class="fill-transparent"></path>
                                <path d="m17.715 15.15.95.316a1 1 0 0 0-1.445-1.185l.495.869ZM9 6.035l.846.534a1 1 0 0 0-1.14-1.49L9 6.035Zm8.221 8.246a5.47 5.47 0 0 1-2.72.718v2a7.47 7.47 0 0 0 3.71-.98l-.99-1.738Zm-2.72.718A5.5 5.5 0 0 1 9 9.5H7a7.5 7.5 0 0 0 7.5 7.5v-2ZM9 9.5c0-1.079.31-2.082.845-2.93L8.153 5.5A7.47 7.47 0 0 0 7 9.5h2Zm-4 3.368C5 10.089 6.815 7.75 9.292 6.99L8.706 5.08C5.397 6.094 3 9.201 3 12.867h2Zm6.042 6.136C7.718 19.003 5 16.268 5 12.867H3c0 4.48 3.588 8.136 8.042 8.136v-2Zm5.725-4.17c-.81 2.433-3.074 4.17-5.725 4.17v2c3.552 0 6.553-2.327 7.622-5.537l-1.897-.632Z" class="fill-current"></path>
                                <path fill-rule="evenodd" clip-rule="evenodd" d="M17 3a1 1 0 0 1 1 1 2 2 0 0 0 2 2 1 1 0 1 1 0 2 2 2 0 0 0-2 2 1 1 0 1 1-2 0 2 2 0 0 0-2-2 1 1 0 1 1 0-2 2 2 0 0 0 2-2 1 1 0 0 1 1-1Z" class="fill-current"></path>
                            </svg>
                            <span v-if="theme === 'light'">Светлая</span>
                            <span v-if="theme === 'dark'">Темная</span>
                            <span v-if="theme === 'system'">Системная</span>
                            <svg class="w-6 h-6 ml-2" fill="none">
                                <path d="m15 11-3 3-3-3" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></path>
                            </svg>
                            <select class="absolute appearance-none inset-0 w-full h-full opacity-0" @change="onChangeTheme">
                                <option value="light" :selected="theme === 'light'">Светлая</option>
                                <option value="dark" :selected="theme === 'dark'">Темная</option>
                                <option value="system" :selected="theme === 'system'">Системная</option>
                            </select>
                        </div>
                    </div>
                </div>
            </DialogPanel>
        </Dialog>
    </TransitionRoot>
</template>
